import React, { useContext, useCallback, useState, useEffect, useMemo } from 'react';
import _ from 'lodash';
import { withViewModelService, ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { wizardProps } from 'wmic-pe-portals-custom-wizard-react';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { useDocumentTitle, useWizardModals, WMICWizardChangeOrRenewalPage } from 'wmic-pe-portals-wizard-components-ui';
import { CONSTANTS, WMICUserAccessUtil, WMICDriverUtil, useCanJumpToWizardStepDispatch } from 'wmic-pe-portals-utils-js';
import { WMICDriverDetailView, useDriverActions } from 'wmic-pe-capability-gateway-common-pa-react';
import { messages as policyChangeCommonMessages } from 'wmic-pe-capability-gateway-policychange-common-react';

import { RISK_PATHS } from 'wmic-pe-portals-utils-js/StringConstants';
import messages from './WMICPCPADriversDetailsPage.messages';
import metadata from './WMICPCPADriversDetailsPage.metadata.json5';
import { TranslatorContext } from '@jutro/legacy/locale';

function WMICPCPADriversDetailsPage(props) {
    const { wizardData: policyChangeVM, updateWizardData } = props;
    const { setWizardLoading } = useWizardModals();
    const { EndorsementService } = useDependencies('EndorsementService');
    const { authHeader, authUserData } = useAuthentication();
    const translator = useContext(TranslatorContext);
    const [showErrors, setShowErrors] = useState(false);
    const { initialValidation, isComponentValid, onValidate, registerInitialComponentValidation } =
        useValidation('WMICPCPADriversDetailsPage');
    const { wizardStepDispatch } = useCanJumpToWizardStepDispatch();
    const {
        initDriverData,
        onCreateDriver,
        onSaveDriver,
        onSaveDriverMiddleware,
        onDeleteDriver,
        renderAddDriverButton,
    } = useDriverActions({ jobVM: policyChangeVM, updateWizardData, setShowErrors });

    useDocumentTitle(translator(messages.driversTitle), policyChangeVM);

    useEffect(() => {
        registerInitialComponentValidation(() => _.get(policyChangeVM, RISK_PATHS.DRIVERS_PATH).value.length > 0);

        initDriverData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const canEditPolicyChange = useMemo(
        () => WMICUserAccessUtil.canEditPolicyChange(authUserData.permission_Ext),
        [authUserData.permission_Ext]
    );

    const onEditDriver = useCallback(() => {
        _.set(policyChangeVM, 'isEditingPage.value', true);
    }, [policyChangeVM]);

    const onCancelDriver = () => {
        _.set(policyChangeVM, 'isEditingPage.value', false);
        updateWizardData(policyChangeVM);
        wizardStepDispatch({ type: CONSTANTS.WIZARD_ACTION_TYPES.RESET, vm: policyChangeVM });
    };

    const onNext = useCallback(async () => {
        if (!isComponentValid || !WMICDriverUtil.presentDriverList(policyChangeVM)) {
            setShowErrors(true);
            return false;
        }

        if (canEditPolicyChange) {
            try {
                setWizardLoading(true);

                policyChangeVM.value = await EndorsementService.saveEndorsement([policyChangeVM.value], authHeader);

                updateWizardData(policyChangeVM);

                return policyChangeVM;
            } finally {
                setWizardLoading(false);
            }
        }

        return false;
    }, [
        isComponentValid,
        canEditPolicyChange,
        setWizardLoading,
        policyChangeVM,
        EndorsementService,
        authHeader,
        updateWizardData,
    ]);

    const overrideProps = {
        atLeastOneMsgContainer: {
            visible: _.isEmpty(_.get(policyChangeVM, `${RISK_PATHS.DRIVERS_PATH}.value`, [])),
            message: messages.atLeastOne,
        },
        requiredText: {
            visible: _.get(policyChangeVM, RISK_PATHS.DRIVERS_PATH).length > 0,
        },
        driverListView: {
            value: _.get(policyChangeVM, `${RISK_PATHS.DRIVERS_PATH}.children`, []),
            clickable: true,
            startOpen: true,
            readOnly: !canEditPolicyChange,
            VMData: [
                {
                    headerText: translator(messages.driverName),
                    path: 'displayName',
                },
                {
                    headerText: translator(messages.licenseNumber),
                    path: 'licenseNumber',
                },
                {
                    headerText: translator(messages.licenseProvince),
                    path: 'licenseState',
                    getData: (row, path) => _.get(row.value, path) || translator(messages.licenseProvinceOther),
                },
            ],
            toCreate: onCreateDriver,
            toEdit: onEditDriver,
            onSave: onSaveDriver,
            onSaveMiddleware: onSaveDriverMiddleware,
            onDelete: onDeleteDriver,
            toUndoCreate: () => {
                const drivers = _.get(policyChangeVM.value, RISK_PATHS.DRIVERS_PATH);
                drivers.splice(drivers.length - 1, 1);
                _.set(policyChangeVM.value, RISK_PATHS.DRIVERS_PATH, drivers);
                _.set(policyChangeVM, 'isEditingPage.value', false);
                updateWizardData(policyChangeVM);
            },
            detailViewComponent: WMICDriverDetailView,
            onValidate,
            renderAddButton: renderAddDriverButton,
            detailViewComponentProps: {
                jobVM: policyChangeVM,
            },
            onCancel: onCancelDriver,
            showCancelModal: true,
        },
    };

    return (
        <WMICWizardChangeOrRenewalPage
            onNext={onNext}
            cancelLabel={translator(policyChangeCommonMessages.saveAndExit)}
            isSkipping={initialValidation}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={policyChangeVM}
                overrideProps={overrideProps}
                onModelChange={updateWizardData}
                showErrors={showErrors}
                onValidationChange={onValidate}
            />
        </WMICWizardChangeOrRenewalPage>
    );
}

WMICPCPADriversDetailsPage.propTypes = wizardProps;

export default withViewModelService(WMICPCPADriversDetailsPage);
